import type { ISO8601DateOnly } from '../../cross-app-shared/types/common.ts';
import type { UserID } from '../../tri-shared/types/db.ts';
import type {
  HazWasteInspectionKind,
  HazWasteInspectionResponses,
} from '../../tri-shared/types/hazwaste-inspections.ts';

type InspectionChecklistItem = { key: string; label: string };
export type InspectionChecklistGroup = { key: string; title: string; items: InspectionChecklistItem[] };

export const containerInspectionChecklistGroups: InspectionChecklistGroup[] = [
  {
    key: 'areaCondition',
    title: 'Area Condition',
    items: [
      { key: 'noSpillsOrReleases', label: 'No evidence of spills or releases' },
      { key: 'cleanAndOrderly', label: 'Area is clean and orderly' },
      { key: 'accessMaintained', label: 'Required access and aisle space is maintained' },
      { key: 'noObviousHazards', label: 'No obvious hazards or unsafe conditions' },
    ],
  },
  {
    key: 'containerCondition',
    title: 'Containers',
    items: [
      { key: 'notLeaking', label: 'No containers are leaking' },
      {
        key: 'goodCondition',
        label: 'Containers are in good condition with no corrosion, bulging, severe rust, or deterioration',
      },
      { key: 'closedWhenNotInUse', label: 'Containers are closed except when waste is being added or removed' },
      { key: 'storedToPreventRelease', label: 'Containers are handled and stored to prevent rupture or leakage' },
      { key: 'compatibleContainer', label: 'Waste appears compatible with the container' },
      { key: 'incompatibleWastesSeparated', label: 'Incompatible wastes are appropriately separated' },
    ],
  },
  {
    key: 'labelsAndDates',
    title: 'Labeling and Accumulation',
    items: [
      { key: 'hazardousWasteMarking', label: 'Each container is marked "Hazardous Waste"' },
      { key: 'hazardIndications', label: 'Hazard indications are present' },
      { key: 'datesPresentAndLegible', label: 'Accumulation start dates are present and legible' },
      { key: 'withinAccumulationLimit', label: 'No waste has exceeded the applicable accumulation time limit' },
      { key: 'labelsVisibleAndLegible', label: 'Labels and markings are visible and legible' },
    ],
  },
  {
    key: 'areaControls',
    title: 'Area Controls',
    items: [
      { key: 'containmentCondition', label: 'Secondary containment, where required or provided, is in good condition' },
      { key: 'noContainmentLiquids', label: 'No accumulated liquids are present in containment' },
      { key: 'emergencyAccessClear', label: 'Emergency equipment and access routes are unobstructed' },
      { key: 'warningSignsVisible', label: 'Required warning signs are present and visible' },
    ],
  },
];

export const tankInspectionChecklistGroups: InspectionChecklistGroup[] = [
  {
    key: 'tankMonitoring',
    title: 'Monitoring and Leak Detection',
    items: [
      {
        key: 'monitoringDataReviewed',
        label: 'Monitoring and leak detection data, where present, has been reviewed for the operating day',
      },
      { key: 'operatingAccordingToDesign', label: 'Tank system appears to be operating according to its design' },
      {
        key: 'leakDetectionAlertsClear',
        label: 'Leak detection equipment or workplace leak checks show no unaddressed leak condition',
      },
    ],
  },
  {
    key: 'tankSystemCondition',
    title: 'Tank System Condition',
    items: [
      { key: 'overfillSpillControlsWorking', label: 'Overfill and spill control equipment is in good working order' },
      {
        key: 'abovegroundPortionsNoCorrosion',
        label: 'Aboveground portions of the tank system show no corrosion or release of waste',
      },
      {
        key: 'ancillaryEquipmentInspected',
        label: 'Ancillary equipment without secondary containment, where present, has been inspected',
      },
    ],
  },
  {
    key: 'tankSurroundingsContainment',
    title: 'Surroundings and Containment',
    items: [
      {
        key: 'surroundingAreaNoReleaseSigns',
        label:
          'Area immediately surrounding accessible tank system portions shows no wet spots, dead vegetation, erosion, or release signs',
      },
      {
        key: 'secondaryContainmentNoReleaseSigns',
        label: 'Secondary containment system, where present, shows no erosion or signs of hazardous waste release',
      },
      {
        key: 'accessToTankSystemMaintained',
        label: 'Access to externally accessible tank system portions is maintained for inspection',
      },
    ],
  },
  {
    key: 'tankOperatingRecord',
    title: 'Operating Record',
    items: [
      {
        key: 'dailyInspectionDocumented',
        label: 'Inspection items required by 40 CFR 265.195(a) and (b) are documented in the operating record',
      },
      {
        key: 'alternateWeeklyBasisDocumented',
        label: 'Any alternate weekly schedule basis for 265.195(b) items is documented in the operating record',
      },
      {
        key: 'cathodicProtectionScheduleCurrent',
        label: 'Cathodic protection inspections/tests, if present, are current on their required schedule',
      },
    ],
  },
];

export function inspectionChecklistGroups(kind: HazWasteInspectionKind): InspectionChecklistGroup[] {
  return kind === 'tank' ? tankInspectionChecklistGroups : containerInspectionChecklistGroups;
}

export function inspectionChecklistItems(kind: HazWasteInspectionKind): InspectionChecklistItem[] {
  return inspectionChecklistGroups(kind).flatMap((group) => group.items);
}

export function inspectionChecklistItemLabel(kind: HazWasteInspectionKind, key: string): string {
  return inspectionChecklistItems(kind).find((item) => item.key === key)?.label ?? key;
}

/** What will be done about a finding, by when (facility-local), and who is responsible. */
export type HazWasteCorrectiveAction = { description: string; dueAt: ISO8601DateOnly; assignedTo: UserID };

/** One checklist item's answer. A flagged item records what the inspector found and the corrective action. */
export type HazWasteInspectionItemAnswer =
  | { result: 'acceptable' }
  | { result: 'issue'; finding: string; correctiveAction: HazWasteCorrectiveAction };

/** What an inspector records for one area or tank. */
export type HazWasteInspectionAnswers = {
  items: Record<string, HazWasteInspectionItemAnswer>;
  notes: string;
};

/** Why `answers` can't be saved as a complete inspection, or null when they can. */
export function inspectionAnswersError(
  kind: HazWasteInspectionKind,
  answers: HazWasteInspectionAnswers,
): string | null {
  const items = inspectionChecklistItems(kind);
  const known = new Set(items.map((item) => item.key));
  const unknown = Object.keys(answers.items).filter((key) => !known.has(key));
  if (unknown.length > 0) {
    return `Unknown inspection items: ${unknown.join(', ')}.`;
  }
  if (items.some((item) => !answers.items[item.key])) {
    return 'Record an outcome for each inspection item.';
  }
  for (const item of items) {
    const answer = answers.items[item.key]!;
    if (answer.result !== 'issue') continue;
    if (!answer.finding.trim()) {
      return `Describe what was found: ${item.label}.`;
    }
    const error = inspectionCorrectiveActionError(answer.correctiveAction);
    if (error) {
      return `${error} (${item.label})`;
    }
  }
  return null;
}

function isDateOnly(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

/** Why a corrective action is incomplete, or null when it is complete. Whether the assignee may be assigned is checked
 * against the facility's users separately. */
export function inspectionCorrectiveActionError(correctiveAction: {
  description: string;
  dueAt: string;
  assignedTo: UserID | null;
}): string | null {
  if (!correctiveAction.description.trim()) return 'Describe the corrective action.';
  if (!isDateOnly(correctiveAction.dueAt)) return 'Set a due date for the corrective action.';
  if (correctiveAction.assignedTo === null) return 'Assign the corrective action to someone.';
  return null;
}

/** The answers as stored: each item's result, with findings stored separately. */
export function inspectionAnswersResponses(answers: HazWasteInspectionAnswers): HazWasteInspectionResponses {
  return Object.fromEntries(Object.entries(answers.items).map(([key, answer]) => [key, answer.result]));
}
